/**
 * GameplayGuide.js - Comprehensive Gameplay Guide Component
 * 
 * Provides detailed gameplay instructions that can be accessed from the main menu
 * or toggled during gameplay. Includes tabbed interface for easy navigation.
 */

export class GameplayGuide {
    constructor(scene) {
        this.scene = scene;
        this.guideModal = null;
        this.currentTab = 'getting-started';
        this.tabButtons = [];
        this.contentPanels = {};
        this.isVisible = false;
    }

    /**
     * Create and show the gameplay guide modal
     */
    show() {
        if (this.isVisible) return;
        
        console.log('[GameplayGuide] Showing gameplay guide');
        this.isVisible = true;
        
        // Create modal background
        this.guideModal = this.scene.add.group();
        
        // Semi-transparent background
        const background = this.scene.add.graphics();
        background.fillStyle(0x000000, 0.85);
        background.fillRect(0, 0, 1920, 1080);
        background.setInteractive(new Phaser.Geom.Rectangle(0, 0, 1920, 1080), Phaser.Geom.Rectangle.Contains);
        background.on('pointerdown', () => this.hide());
        this.guideModal.add(background);
        
        // Main guide panel
        const panelWidth = 1400;
        const panelHeight = 900;
        const panelX = 960 - panelWidth/2;
        const panelY = 540 - panelHeight/2;
        
        // Panel background with border
        const panel = this.scene.add.graphics();
        panel.fillStyle(0x001122, 0.95);
        panel.fillRect(panelX, panelY, panelWidth, panelHeight);
        panel.lineStyle(3, 0x00ff00);
        panel.strokeRect(panelX, panelY, panelWidth, panelHeight);
        this.guideModal.add(panel);
        
        // Title
        const title = this.scene.add.text(960, panelY + 40, '🎮 MEMEX RACING - COMPLETE GAMEPLAY GUIDE', {
            fontSize: '28px',
            fill: '#0088ff',
            fontFamily: 'Courier New, monospace',
            fontStyle: 'bold'
        });
        title.setOrigin(0.5, 0.5);
        this.guideModal.add(title);
        
        // Close button
        const closeButton = this.createButton(panelX + panelWidth - 80, panelY + 40, 'X', '#ff4444', () => this.hide());
        this.guideModal.add(closeButton);
        
        // Create tabs
        this.createTabs(panelX, panelY + 80, panelWidth);
        
        // Create content area
        this.createContentArea(panelX, panelY + 140, panelWidth, panelHeight - 180);
        
        // Show initial tab content
        this.showTabContent(this.currentTab);
        
        // Set depth to ensure it's on top
        this.guideModal.setDepth(1000);
    }

    /**
     * Hide the gameplay guide modal
     */
    hide() {
        if (!this.isVisible) return;
        
        console.log('[GameplayGuide] Hiding gameplay guide');
        this.isVisible = false;
        
        // Clean up event listeners first
        this.cleanupEventListeners();
        
        if (this.guideModal) {
            this.guideModal.destroy();
            this.guideModal = null;
        }
        
        this.tabButtons = [];
        this.contentPanels = {};
    }

    /**
     * Switch to different tab without recreating the modal
     */
    switchTab(tabKey) {
        if (this.currentTab === tabKey || !this.isVisible) return;
        
        console.log(`[GameplayGuide] Switching to tab: ${tabKey}`);
        this.currentTab = tabKey;
        
        // Update tab button visuals
        this.updateTabVisuals();
        
        // Show new tab content
        this.showTabContent(tabKey);
    }

    /**
     * Update tab button visual states
     */
    updateTabVisuals() {
        const tabs = [
            { key: 'getting-started', label: 'Getting Started', color: '#00ff00' },
            { key: 'race-mechanics', label: 'Race Mechanics', color: '#0088ff' },
            { key: 'power-ups', label: 'Power-ups & Obstacles', color: '#ff8800' },
            { key: 'betting', label: 'Betting System', color: '#ffff00' },
            { key: 'strategy', label: 'Strategy Tips', color: '#ff00ff' },
            { key: 'controls', label: 'Controls & Debug', color: '#00ffff' }
        ];

        this.tabButtons.forEach((tabButton, index) => {
            const tab = tabs[index];
            const isActive = this.currentTab === tabButton.key; // Use tabButton.key instead of tab.key
            const bgColor = isActive ? tab.color : '#333333';
            const textColor = isActive ? '#000000' : tab.color;
            
            // Update background color using absolute coordinates (matching original creation)
            tabButton.bg.clear();
            tabButton.bg.fillStyle(bgColor === '#333333' ? 0x333333 : parseInt(tab.color.replace('#', ''), 16), 0.8);
            tabButton.bg.fillRect(tabButton.bounds.x, tabButton.bounds.y, tabButton.bounds.width, tabButton.bounds.height);
            tabButton.bg.lineStyle(1, parseInt(tab.color.replace('#', ''), 16));
            tabButton.bg.strokeRect(tabButton.bounds.x, tabButton.bounds.y, tabButton.bounds.width, tabButton.bounds.height);
            
            // Update text color
            tabButton.text.setFill(textColor);
        });
    }

    /**
     * Clean up event listeners to prevent memory leaks
     */
    cleanupEventListeners() {
        // Remove all interactive event listeners
        this.tabButtons.forEach(tabButton => {
            if (tabButton.hitArea) {
                try {
                    tabButton.hitArea.off('pointerdown');
                    tabButton.hitArea.off('pointerover');
                    tabButton.hitArea.off('pointerout');
                } catch (error) {
                    // Ignore cleanup errors for already destroyed objects
                    console.debug('[GameplayGuide] Event cleanup warning:', error.message);
                }
            }
        });
        
        // Clear any background click handlers
        if (this.guideModal) {
            try {
                this.guideModal.list.forEach(child => {
                    if (child.input && child.input.enabled) {
                        child.off('pointerdown');
                    }
                });
            } catch (error) {
                console.debug('[GameplayGuide] Background cleanup warning:', error.message);
            }
        }
    }

    /**
     * Create tab navigation
     */
    createTabs(startX, startY, panelWidth) {
        const tabs = [
            { key: 'getting-started', label: 'Getting Started', color: '#00ff00' },
            { key: 'race-mechanics', label: 'Race Mechanics', color: '#0088ff' },
            { key: 'power-ups', label: 'Power-ups & Obstacles', color: '#ff8800' },
            { key: 'betting', label: 'Betting System', color: '#ffff00' },
            { key: 'strategy', label: 'Strategy Tips', color: '#ff00ff' },
            { key: 'controls', label: 'Controls & Debug', color: '#00ffff' }
        ];
        
        const tabWidth = (panelWidth - 60) / tabs.length;
        
        tabs.forEach((tab, index) => {
            const x = startX + 30 + (tabWidth * index) + tabWidth/2;
            const y = startY;
            
            const isActive = this.currentTab === tab.key;
            const bgColor = isActive ? tab.color : '#333333';
            const textColor = isActive ? '#000000' : tab.color;
            
            // Tab background
            const tabBg = this.scene.add.graphics();
            tabBg.fillStyle(bgColor === '#333333' ? 0x333333 : parseInt(tab.color.replace('#', ''), 16), 0.8);
            tabBg.fillRect(x - tabWidth/2 + 5, y - 15, tabWidth - 10, 30);
            tabBg.lineStyle(1, parseInt(tab.color.replace('#', ''), 16));
            tabBg.strokeRect(x - tabWidth/2 + 5, y - 15, tabWidth - 10, 30);
            
            // Tab text
            const tabText = this.scene.add.text(x, y, tab.label, {
                fontSize: '14px',
                fill: textColor,
                fontFamily: 'Courier New, monospace',
                fontStyle: 'bold'
            });
            tabText.setOrigin(0.5, 0.5);
            
            // Make tab interactive
            const tabHitArea = this.scene.add.graphics();
            tabHitArea.fillStyle(0x000000, 0.01); // Nearly transparent
            tabHitArea.fillRect(x - tabWidth/2 + 5, y - 15, tabWidth - 10, 30);
            tabHitArea.setInteractive(new Phaser.Geom.Rectangle(0, 0, tabWidth - 10, 30), Phaser.Geom.Rectangle.Contains);
            tabHitArea.on('pointerdown', () => {
                this.switchTab(tab.key);
            });
            
            this.guideModal.add([tabBg, tabText, tabHitArea]);
            this.tabButtons.push({ 
                key: tab.key, 
                bg: tabBg, 
                text: tabText, 
                hitArea: tabHitArea,
                bounds: { x: x - tabWidth/2 + 5, y: y - 15, width: tabWidth - 10, height: 30 }
            });
        });
    }

    /**
     * Create content area for tab content
     */
    createContentArea(startX, startY, panelWidth, contentHeight) {
        // Create scrollable content area background
        const contentBg = this.scene.add.graphics();
        contentBg.fillStyle(0x000011, 0.9);
        contentBg.fillRect(startX + 20, startY, panelWidth - 40, contentHeight);
        contentBg.lineStyle(1, 0x00ff00);
        contentBg.strokeRect(startX + 20, startY, panelWidth - 40, contentHeight);
        this.guideModal.add(contentBg);
        
        // Store content area bounds for later use
        this.contentArea = {
            x: startX + 40,
            y: startY + 20,
            width: panelWidth - 80,
            height: contentHeight - 40
        };
    }

    /**
     * Show content for specific tab
     */
    showTabContent(tabKey) {
        // Clear existing content
        Object.values(this.contentPanels).forEach(panel => {
            if (panel) panel.destroy();
        });
        this.contentPanels = {};

        const content = this.getTabContent(tabKey);
        this.createScrollableContent(content);
    }

    /**
     * Create scrollable content with better performance
     */
    createScrollableContent(content) {
        const contentGroup = this.scene.add.group();
        let yOffset = 0;
        
        // Create a clipping mask for scrollable content
        const maskShape = this.scene.add.graphics();
        maskShape.fillStyle(0xffffff);
        maskShape.fillRect(this.contentArea.x, this.contentArea.y, this.contentArea.width, this.contentArea.height);
        const mask = maskShape.createGeometryMask();
        
        content.forEach(item => {
            let element;
            
            if (item.type === 'title') {
                element = this.scene.add.text(this.contentArea.x, this.contentArea.y + yOffset, item.text, {
                    fontSize: '16px',
                    fill: item.color || '#ffff00',
                    fontFamily: 'Courier New, monospace',
                    fontStyle: 'bold'
                });
                yOffset += 30;
            } else if (item.type === 'text') {
                element = this.scene.add.text(this.contentArea.x, this.contentArea.y + yOffset, item.text, {
                    fontSize: '11px',
                    fill: item.color || '#00ff00',
                    fontFamily: 'Courier New, monospace',
                    wordWrap: { width: this.contentArea.width - 20 }
                });
                yOffset += element.height + 8;
            } else if (item.type === 'bullet') {
                const bullet = this.scene.add.text(this.contentArea.x + 15, this.contentArea.y + yOffset, '• ' + item.text, {
                    fontSize: '10px',
                    fill: item.color || '#00ff00',
                    fontFamily: 'Courier New, monospace',
                    wordWrap: { width: this.contentArea.width - 50 }
                });
                yOffset += bullet.height + 4;
                element = bullet;
            } else if (item.type === 'tip') {
                const tipBg = this.scene.add.graphics();
                tipBg.fillStyle(0x4d3300, 0.3);
                tipBg.fillRect(this.contentArea.x - 5, this.contentArea.y + yOffset - 2, this.contentArea.width - 10, 22);
                tipBg.lineStyle(1, 0xffa500);
                tipBg.strokeRect(this.contentArea.x - 5, this.contentArea.y + yOffset - 2, this.contentArea.width - 10, 22);
                
                const tipText = this.scene.add.text(this.contentArea.x + 5, this.contentArea.y + yOffset + 2, '💡 ' + item.text, {
                    fontSize: '10px',
                    fill: '#ffa500',
                    fontFamily: 'Courier New, monospace',
                    fontStyle: 'bold',
                    wordWrap: { width: this.contentArea.width - 40 }
                });
                
                contentGroup.add([tipBg, tipText]);
                yOffset += 26;
                element = tipText;
            } else if (item.type === 'warning') {
                const warnBg = this.scene.add.graphics();
                warnBg.fillStyle(0x4d0000, 0.3);
                warnBg.fillRect(this.contentArea.x - 5, this.contentArea.y + yOffset - 2, this.contentArea.width - 10, 22);
                warnBg.lineStyle(1, 0xff4444);
                warnBg.strokeRect(this.contentArea.x - 5, this.contentArea.y + yOffset - 2, this.contentArea.width - 10, 22);
                
                const warnText = this.scene.add.text(this.contentArea.x + 5, this.contentArea.y + yOffset + 2, '⚠️ ' + item.text, {
                    fontSize: '10px',
                    fill: '#ff4444',
                    fontFamily: 'Courier New, monospace',
                    fontStyle: 'bold',
                    wordWrap: { width: this.contentArea.width - 40 }
                });
                
                contentGroup.add([warnBg, warnText]);
                yOffset += 26;
                element = warnText;
            }
            
            if (element) {
                contentGroup.add(element);
            }
        });
        
        // Apply mask to content group for scrolling effect
        contentGroup.setMask(mask);
        
        // Add scroll indicator if content exceeds area height
        if (yOffset > this.contentArea.height) {
            const scrollIndicator = this.scene.add.text(
                this.contentArea.x + this.contentArea.width - 100, 
                this.contentArea.y + this.contentArea.height - 20,
                '(Content scrolls)', {
                fontSize: '9px',
                fill: '#888888',
                fontFamily: 'Courier New, monospace',
                fontStyle: 'italic'
            });
            contentGroup.add(scrollIndicator);
        }
        
        this.contentPanels[this.currentTab] = contentGroup;
        this.guideModal.add(contentGroup);
    }

    /**
     * Get content for specific tab
     */
    getTabContent(tabKey) {
        const contentMap = {
            'getting-started': [
                { type: 'title', text: '🚀 Getting Started with Memex Racing', color: '#00ff00' },
                { type: 'text', text: 'Welcome to Memex Racing - an AI-controlled racing game with betting mechanics!' },
                { type: 'title', text: 'Registration & Setup:', color: '#0088ff' },
                { type: 'bullet', text: 'Enter your name to create a player profile' },
                { type: 'bullet', text: 'Receive 10,000 starting tokens automatically' },
                { type: 'bullet', text: 'Choose your pug character avatar (pug1-4)' },
                { type: 'title', text: 'Joining a Race:', color: '#0088ff' },
                { type: 'bullet', text: 'Real players pay 100 tokens entry fee' },
                { type: 'bullet', text: 'AI players pay only 50 tokens (subsidized)' },
                { type: 'bullet', text: 'Races support 1-6 players total' },
                { type: 'bullet', text: 'Winner takes 90% of the prize pool!' },
                { type: 'tip', text: 'Start with small bets to learn the game mechanics before risking large amounts!' }
            ],
            'race-mechanics': [
                { type: 'title', text: '🏁 Race Mechanics & Rules', color: '#0088ff' },
                { type: 'title', text: 'Movement System:', color: '#ffff00' },
                { type: 'bullet', text: '"Blind Horse" AI - No pathfinding, pure randomness' },
                { type: 'bullet', text: 'Players cannot directly control their characters' },
                { type: 'bullet', text: 'Random movement with momentum creates chaos' },
                { type: 'bullet', text: 'Unpredictable outcomes make every race exciting' },
                { type: 'title', text: 'Win Conditions:', color: '#ffff00' },
                { type: 'bullet', text: 'First player to reach the M token wins the race' },
                { type: 'bullet', text: '5-minute time limit for all races' },
                { type: 'bullet', text: 'If nobody reaches the token, all players lose' },
                { type: 'bullet', text: 'Winner receives 90% of total prize pool' },
                { type: 'title', text: 'Starting Setup:', color: '#ffff00' },
                { type: 'bullet', text: 'All players start at the same random location' },
                { type: 'bullet', text: 'Characters are slightly spread out to avoid clustering' },
                { type: 'bullet', text: 'M token spawns at a random location far from start' },
                { type: 'bullet', text: 'Fair competition - everyone has equal chance' }
            ],
            'power-ups': [
                { type: 'title', text: '⚡ Power-ups & Obstacles', color: '#ff8800' },
                { type: 'title', text: 'Speed Boosters (8 Types):', color: '#ffff00' },
                { type: 'bullet', text: 'Antenna Booster - 1.4x speed (4-7 seconds)', color: '#00ff88' },
                { type: 'bullet', text: 'Twitter Post - 1.5x speed (4-7 seconds)', color: '#00ff88' },
                { type: 'bullet', text: 'Memex Tag - 1.6x speed (4-7 seconds)', color: '#00ff88' },
                { type: 'bullet', text: 'Poo - 1.7x speed (4-7 seconds)', color: '#00ff88' },
                { type: 'bullet', text: 'Toilet Paper - 1.7x speed (4-7 seconds)', color: '#00ff88' },
                { type: 'bullet', text: 'Toilet - 1.8x speed (4-7 seconds)', color: '#00ff88' },
                { type: 'bullet', text: 'Banana - 1.9x speed (4-7 seconds)', color: '#00ff88' },
                { type: 'bullet', text: 'King Kong - 2.0x speed (4-7 seconds)', color: '#00ff88' },
                { type: 'title', text: 'Special Skills (5 Types):', color: '#ffff00' },
                { type: 'bullet', text: 'Thunder - Paralyze 3 random opponents (3 seconds)', color: '#ffff00' },
                { type: 'bullet', text: 'Fire - Burn 2 characters, causing slowdown', color: '#ffff00' },
                { type: 'bullet', text: 'Bubble - Bounce protection from other players (8s)', color: '#ffff00' },
                { type: 'bullet', text: 'Magnet - Attract to nearby racers (5 seconds)', color: '#ffff00' },
                { type: 'bullet', text: 'Teleport - Random teleport ALL players on map', color: '#ffff00' },
                { type: 'title', text: 'Obstacles & Hazards:', color: '#ff4444' },
                { type: 'bullet', text: 'Oil Slick - Uncontrollable spinning, 30% speed (2s)', color: '#ff8888' },
                { type: 'bullet', text: 'Mud Patch - 40% speed reduction while crossing', color: '#ff8888' },
                { type: 'bullet', text: 'Rock Barrier - Complete stop on collision', color: '#ff8888' },
                { type: 'bullet', text: 'Spike Strip - Instant 50% speed reduction (3s)', color: '#ff8888' },
                { type: 'bullet', text: 'Moving Barrier - Oscillates, knocks players back', color: '#ff8888' },
                { type: 'bullet', text: 'Rotating Blade - Pushes players in random direction', color: '#ff8888' },
                { type: 'bullet', text: 'Laser Beam - 1 second stun with warning flash', color: '#ff8888' },
                { type: 'bullet', text: 'Trap Door - Player falls, stuck for 2 seconds', color: '#ff8888' },
                { type: 'bullet', text: 'Ice Patch - Loss of directional control (1.5s slide)', color: '#ff8888' },
                { type: 'bullet', text: 'Magnetic Field - Pulls players toward center', color: '#ff8888' },
                { type: 'warning', text: 'Large Barriers (200x400px) can block entire paths with 3s warnings!' }
            ],
            'betting': [
                { type: 'title', text: '💰 Complete Betting System', color: '#ffff00' },
                { type: 'title', text: 'Entry Fees:', color: '#00ff88' },
                { type: 'bullet', text: 'Real players: 100 tokens entry fee' },
                { type: 'bullet', text: 'AI players: 50 tokens entry fee (subsidized)' },
                { type: 'bullet', text: 'Winner takes 90% of total prize pool' },
                { type: 'bullet', text: 'House keeps 10% commission' },
                { type: 'title', text: 'Side Betting:', color: '#00ff88' },
                { type: 'bullet', text: 'Bet 50-1000 tokens on any player in the race' },
                { type: 'bullet', text: 'Multiple bets allowed per race' },
                { type: 'bullet', text: 'Independent of entry fee' },
                { type: 'bullet', text: 'Win if your chosen player wins the race' },
                { type: 'title', text: 'Spectator Betting:', color: '#00ff88' },
                { type: 'bullet', text: 'Fixed 200 tokens per spectator bet' },
                { type: 'bullet', text: 'Maximum one bet per race' },
                { type: 'bullet', text: 'Winners split the spectator pool equally' },
                { type: 'bullet', text: '5% house fee on spectator winnings' },
                { type: 'bullet', text: '90% refund if no spectators win' },
                { type: 'title', text: 'Example Prize Pool (6 players):', color: '#0088ff' },
                { type: 'text', text: '• 3 Real players: 3 × 100 = 300 tokens\n• 3 AI players: 3 × 50 = 150 tokens\n• Total pool: 450 tokens\n• Winner gets: 405 tokens (90%)\n• Net profit for winner: 305 tokens (405 - 100 entry)' },
                { type: 'title', text: 'Token Management:', color: '#00ff88' },
                { type: 'bullet', text: 'Starting balance: 10,000 tokens for new players' },
                { type: 'bullet', text: 'Emergency loans: 500 tokens when balance reaches 0' },
                { type: 'bullet', text: '50% auto-repayment from future winnings' },
                { type: 'bullet', text: 'Mini-games available to earn more tokens' }
            ],
            'strategy': [
                { type: 'title', text: '🧠 Strategy & Advanced Tips', color: '#ff00ff' },
                { type: 'title', text: 'Basic Strategy:', color: '#ffff00' },
                { type: 'bullet', text: 'Watch for skill timing - Thunder and Teleport change everything' },
                { type: 'bullet', text: 'Barrier warnings = opportunity or danger' },
                { type: 'bullet', text: 'Collect items for points even if not winning' },
                { type: 'bullet', text: 'Sometimes slower movement avoids more hazards' },
                { type: 'bullet', text: 'Manage chaos rather than trying to control it' },
                { type: 'title', text: 'Advanced Tactics:', color: '#ffff00' },
                { type: 'bullet', text: 'Study AI movement patterns over multiple races' },
                { type: 'bullet', text: 'Predict obstacle spawn timing based on game clock' },
                { type: 'bullet', text: 'Use skills strategically - timing is everything' },
                { type: 'bullet', text: 'Bankroll management with side bets' },
                { type: 'bullet', text: 'Watch for stuck detection teleports near M token' },
                { type: 'title', text: 'Difficulty Escalation (Time-based):', color: '#ffff00' },
                { type: 'bullet', text: '4-5 minutes: Normal spawn rates for all items' },
                { type: 'bullet', text: '2-4 minutes: 2x spawn rates for obstacles and power-ups' },
                { type: 'bullet', text: '0-2 minutes: Maximum chaos mode - anything can happen!' },
                { type: 'bullet', text: 'Final minute is the most unpredictable' },
                { type: 'tip', text: 'The random nature means anyone can win - players behind can suddenly teleport near the M token!' },
                { type: 'warning', text: 'Speed boosters can backfire - faster into obstacles means harder crashes!' }
            ],
            'controls': [
                { type: 'title', text: '🎯 Controls & Debug Features', color: '#00ffff' },
                { type: 'title', text: 'Desktop Controls:', color: '#ffff00' },
                { type: 'bullet', text: 'ESC - Return to lobby or main menu' },
                { type: 'bullet', text: 'D - Toggle debug mode (position, speed, stuck counter)' },
                { type: 'bullet', text: 'H or G - Toggle this gameplay guide (during races)' },
                { type: 'bullet', text: 'Click "Play Again?" - Start new race after game ends' },
                { type: 'bullet', text: 'Click "No" - Return to main menu after race' },
                { type: 'title', text: 'Mobile Controls:', color: '#ffff00' },
                { type: 'bullet', text: 'Swipe up/down - Expand/collapse bottom betting panel' },
                { type: 'bullet', text: 'Tap panel headers - Toggle individual panel sections' },
                { type: 'bullet', text: 'Pinch gesture - Disabled to prevent accidental zoom' },
                { type: 'bullet', text: 'Hamburger menu (☰) - Access restart, settings, sound toggle' },
                { type: 'bullet', text: 'Touch slider - Adjust bet amounts with visual feedback' },
                { type: 'bullet', text: 'Large touch targets - All buttons optimized for fingers' },
                { type: 'title', text: 'Debug Mode Features (Press D):', color: '#ffff00' },
                { type: 'bullet', text: 'Real-time player position, direction, and speed display' },
                { type: 'bullet', text: 'Heat map of problematic areas where players get stuck' },
                { type: 'bullet', text: 'Visual indicators for movement direction and status' },
                { type: 'bullet', text: 'Stuck counter showing how long player has been stuck' },
                { type: 'bullet', text: 'Top 3 problem areas with occurrence count' },
                { type: 'bullet', text: 'Last 10 positions tracking for stuck detection' },
                { type: 'title', text: 'System Requirements:', color: '#ffff00' },
                { type: 'bullet', text: 'Modern web browser (Chrome 90+, Firefox 88+, Safari 14+)' },
                { type: 'bullet', text: 'JavaScript enabled' },
                { type: 'bullet', text: 'HTTP server (not file:// protocol)' },
                { type: 'bullet', text: '~2MB download for all game assets' },
                { type: 'bullet', text: 'Stable internet connection for multiplayer features' }
            ]
        };

        return contentMap[tabKey] || [];
    }

    /**
     * Create a button
     */
    createButton(x, y, text, color, callback) {
        const button = this.scene.add.text(x, y, text, {
            fontSize: '18px',
            fill: color,
            fontFamily: 'Courier New, monospace',
            fontStyle: 'bold',
            backgroundColor: 'rgba(0,0,0,0.5)',
            padding: { x: 10, y: 5 }
        });
        button.setOrigin(0.5, 0.5);
        button.setInteractive();
        button.on('pointerdown', callback);
        button.on('pointerover', () => button.setScale(1.1));
        button.on('pointerout', () => button.setScale(1.0));
        
        return button;
    }

    /**
     * Toggle guide visibility
     */
    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }
}